import { query } from "./strapi";

// Types for form structure
export interface FormField {
  id: number;
  label: string;
  slug: string;
  type: string;
  isRequired: boolean;
  order: number;
  placeholder?: string;
  helpText?: string;
  options?: any;
  validation?: any;
  defaultValue?: string;
  width: "w1/4" | "w1/3" | "w1/2" | "w2/3" | "full";
  fullRow?: boolean;
}

export interface FormSection {
  id: number;
  title: string;
  order: number;
  description?: string;
  fields: FormField[];
}

export interface Form {
  id: number;
  title: string;
  url: string;
  description?: string;
  sections: FormSection[];
  submitButtonLabel: string;
  redirectUrl?: string;
  successTitle?: string;
  successText?: string;
}

// Get form by URL
export async function getFormByUrl(url: string): Promise<Form | null> {
  try {
    const response = await query(`forms/url/${encodeURIComponent(url)}`, {
      method: "GET",
    });

    console.log("Form API response:", response); // Add this debug log

    if (!response) {
      console.error("Empty API response");
      return null;
    }

    // Handle different response structures from Strapi
    let formData = response;

    // If the response has a 'data' property (Strapi's REST API format)
    if (response.data) {
      formData = response.data;
    }

    // If we don't have an 'id' property directly, the form might not be found
    if (!formData.id) {
      console.error("Invalid form data structure", formData);
      return null;
    }

    // Transform the data to match our interface
    return {
      id: formData.id,
      title: formData.attributes?.title || formData.title,
      url: formData.attributes?.url || formData.url,
      description: formData.attributes?.description || formData.description,
      submitButtonLabel:
        formData.attributes?.submitButtonLabel ||
        formData.submitButtonLabel ||
        "Submit",
      redirectUrl: formData.attributes?.redirectUrl || formData.redirectUrl,
      successTitle: formData.attributes?.successTitle || formData.successTitle,
      successText: formData.attributes?.successText || formData.successText,
      sections: (formData.attributes?.sections || formData.sections || []).map(
        (section: any) => ({
          id: section.id,
          title: section.title,
          order: section.order,
          description: section.description,
          fields: (section.fields || []).map((field: any) => {
            // Process options field to ensure it's properly formatted
            let processedOptions = field.options;
            if (typeof field.options === "string") {
              try {
                processedOptions = JSON.parse(field.options);
              } catch (e) {
                console.error("Error parsing options JSON:", e);
              }
            }

            return {
              id: field.id,
              label: field.label,
              slug: field.slug,
              type: field.type,
              isRequired: field.isRequired,
              order: field.order,
              placeholder: field.placeholder,
              helpText: field.helpText,
              options: processedOptions,
              validation: field.validation,
              defaultValue: field.defaultValue,
              width: field.width,
              fullRow: field.fullRow || false,
            };
          }),
        })
      ),
    };
  } catch (error) {
    console.error("Error fetching form data:", error);
    return null;
  }
}

// Submit form data
export async function submitFormData(
  formId: number,
  formData: any
): Promise<{ success: boolean; id?: number; error?: string }> {
  try {
    const response = await query(`form-submissions/submit/${formId}`, {
      method: "POST",
      body: JSON.stringify({
        data: formData,
      }),
    });

    if (!response || !response.success) {
      return {
        success: false,
        error: response?.error || "Failed to submit form",
      };
    }

    return {
      success: true,
      id: response.id,
    };
  } catch (error) {
    console.error("Error submitting form data:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "An unknown error occurred",
    };
  }
}
